import { allergensNames } from '@/enums/Allergens';
import type { ICategory } from '@/interfaces/ICategory';
import type { IItem } from '@/interfaces/IItem';
import type { IItemOption } from '@/interfaces/IItemOption';
import type { IOption } from '@/interfaces/IOption';
import type { IOptionItem } from '@/interfaces/IOptionItem';
import type { ISimpleHourRange } from '@/interfaces/ISimpleHourRange';

/**
 * The marked allergens of a bitmask as stable English slugs («gluten»,
 * «dairy»…: the `allergens.*` key names); null when none is marked.
 */
export function allergenSlugs(bits: number | null | undefined): string[] | null {
  if (!bits) return null;
  const slugs = Object.entries(allergensNames)
    .filter(([bit]) => (bits & Number(bit)) === Number(bit))
    .map(([, key]) => key.replace(/^allergens\./, ''));
  return slugs.length ? slugs : null;
}

/** The product's own VAT / IGV (%); null when it has none. */
export function itemVat(vat: number | string | null | undefined): number | null {
  if (vat === null || vat === undefined || vat === '') return null;
  const value = Number(vat);
  return Number.isFinite(value) ? value : null;
}

export interface IGpthelpAdminCategorySummary {
  categoryId: number;
  name: string;
  menuId: number | null;
  visible: boolean;
  posOnly: boolean;
  index: number;
  picture: string | null;
  /** The owner kept its products out of «Lo más pedido». */
  notInPopular?: true;
}

export interface IGpthelpAdminCategoryDetails
  extends IGpthelpAdminCategorySummary {
  description: string | null;
  hiddenBranchesIds: string[] | null;
  hourRanges: ISimpleHourRange[] | null;
}

export interface IGpthelpAdminItemSummary {
  itemId: number;
  name: string;
  description: string | null;
  categoryId: number;
  categoryName: string | null;
  menuId: number | null;
  visible: boolean;
  hasOptions: boolean;
  price: number | null;
  discountedPrice: number | null;
  picture: string | null;
  index: number | null;
  /** The owner kept it out of «Lo más pedido» (itself; its category's flag is on the category). */
  notInPopular?: true;
}

export interface IGpthelpAdminItemOptionItemDetails {
  id: number | null;
  title: string;
  /** Sale price (option_item.cost): absolute for price variants, a surcharge for extras; NOT a stock cost. */
  price: number | null;
  discountedPrice: number | null;
  min: number | null;
  max: number | null;
  default: number | null;
  active: boolean;
}

export interface IGpthelpAdminItemOptionDetails {
  id: number | null;
  title: string;
  type: string | null;
  main: boolean;
  mandatory: boolean;
  multiple: boolean;
  min: number | null;
  max: number | null;
  /** false = the group is paused, or hidden on this product. */
  active: boolean;
  /** Linked groups only: whether the group shows on this product (the link's state). */
  activeOnProduct?: boolean;
  /** Linked groups only: how many products link the group. */
  usedByCount?: number;
  optionItems: IGpthelpAdminItemOptionItemDetails[];
}

/** Group id → ids of the products that link it (`optionUsage`). */
export type TGpthelpOptionUsage = Readonly<Record<number, readonly number[]>>;

export interface IGpthelpAdminItemDetails extends IGpthelpAdminItemSummary {
  /** Free-text kitchen notes (item.recipe); NOT the stock recipe. */
  preparationNotes: string | null;
  hourRanges: ISimpleHourRange[] | null;
  hiddenBranchesIds: string[] | null;
  options: IGpthelpAdminItemOptionDetails[];
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const toNullableNumber = (value: unknown): number | null =>
  isFiniteNumber(value) ? value : null;

const toNullableString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value : null;

export function buildEntitiesById<T extends { id: number }>(
  entities: T[]
): Record<number, T> {
  return entities.reduce((acc, e) => {
    acc[e.id] = e;
    return acc;
  }, {} as Record<number, T>);
}

export function categoryToSummary(params: {
  category: ICategory;
}): IGpthelpAdminCategorySummary {
  const { category } = params;
  return {
    categoryId: category.id,
    name: category.name ?? '',
    menuId: toNullableNumber(category.menuId) ?? null,
    visible: !!category.visible,
    posOnly: !!category.posOnly,
    index: toNullableNumber(category.index) ?? 0,
    picture: toNullableString(category.picture) ?? null,
    ...(category.popularHidden ? { notInPopular: true as const } : {}),
  };
}

export function categoryToDetails(params: {
  category: ICategory;
}): IGpthelpAdminCategoryDetails {
  const { category } = params;
  return {
    ...categoryToSummary(params),
    description: toNullableString(category.description) ?? null,
    hiddenBranchesIds: Array.isArray(category.hiddenBranchesIds)
      ? category.hiddenBranchesIds
      : null,
    hourRanges: Array.isArray(category.hourRanges) ? category.hourRanges : null,
  };
}

function optionItemToDetails(
  oi: IOptionItem
): IGpthelpAdminItemOptionItemDetails {
  return {
    id: toNullableNumber(oi.id) ?? null,
    title: oi.title ?? '',
    price: toNullableNumber(oi.cost) ?? null,
    discountedPrice: toNullableNumber(oi.discountedCost) ?? null,
    min: toNullableNumber(oi.min) ?? null,
    max: toNullableNumber(oi.max) ?? null,
    default: toNullableNumber(oi.default) ?? null,
    active: !!oi.active,
  };
}

function compareByIndexThenId(
  a: { index?: number; id?: number },
  b: { index?: number; id?: number }
): number {
  const aIndex = toNullableNumber(a.index) ?? 0;
  const bIndex = toNullableNumber(b.index) ?? 0;
  if (aIndex !== bIndex) return aIndex - bIndex;

  const aId = toNullableNumber(a.id) ?? 0;
  const bId = toNullableNumber(b.id) ?? 0;
  return aId - bId;
}

/** The option's active choices, in menu order. */
export function activeOptionItems(option: IOption): IOptionItem[] {
  return (option.optionItems ?? [])
    .filter((oi) => !!oi && !!oi.active)
    .sort(compareByIndexThenId);
}

/** The option's choices that are not removed, paused ones included, in menu order. */
export function liveOptionItems(option: IOption): IOptionItem[] {
  return (option.optionItems ?? [])
    .filter((oi) => !!oi && !oi.deletedAt)
    .sort(compareByIndexThenId);
}

/** A group a product shows: one of its own (the sizes) or a linked one. */
export interface IGpthelpItemOptionEntry {
  option: IOption;
  /** The product's link to the group; null for the product's own options. */
  link: IItemOption | null;
}

type TLiveOptionLink = IItemOption & { option: IOption };

const isLiveOptionLink = (link: IItemOption): link is TLiveOptionLink =>
  !!link.option && !link.deleted && !link.option.deletedAt && !link.option.main;

/**
 * Every group of the product that is not removed, paused or hidden ones
 * included: its own options (the sizes) in menu order, then the linked
 * groups in the product's order.
 */
export function allItemOptions(item: IItem): IGpthelpItemOptionEntry[] {
  const own = (item.options ?? [])
    .filter((option) => !!option && !option.deletedAt)
    .sort(compareByIndexThenId)
    .map((option) => ({ option, link: null }));
  const linked = (item.itemOptions ?? [])
    .filter(isLiveOptionLink)
    .sort(
      (a, b) =>
        (toNullableNumber(a.index) ?? 0) - (toNullableNumber(b.index) ?? 0) ||
        (toNullableNumber(a.option.id) ?? 0) -
          (toNullableNumber(b.option.id) ?? 0)
    )
    .map((link) => ({ option: link.option, link }));
  return [...own, ...linked];
}

function optionToDetails(
  entry: IGpthelpItemOptionEntry,
  usage: TGpthelpOptionUsage
): IGpthelpAdminItemOptionDetails {
  const { option, link } = entry;
  // The rule the product applies: the link's overrides on the group's.
  const effective = (link && mergeItemOptionLink(link)) ?? option;
  const id = toNullableNumber(option.id) ?? null;

  return {
    id,
    title: option.title ?? '',
    type: typeof option.type === 'string' ? option.type : null,
    main: !!option.main,
    mandatory: !!effective.mandatory,
    multiple: !!effective.multiple,
    min: toNullableNumber(effective.min) ?? null,
    max: toNullableNumber(effective.max) ?? null,
    // Paused everywhere, or hidden on this product by its link.
    active: !!option.active && !!effective.active,
    ...(link
      ? {
          activeOnProduct: !!effective.active,
          usedByCount: id !== null ? usage[id]?.length ?? 0 : 0,
        }
      : {}),
    optionItems: liveOptionItems(option).map(optionItemToDetails),
  };
}

function mergeItemOptionLink(link: IItemOption): IOption | null {
  const base = link.option;
  if (!base) return null;

  // Merge link overrides to reflect the effective option constraints on this item.
  const merged: IOption = {
    ...base,
    mandatory: link.mandatory ?? base.mandatory,
    multiple: link.multiple ?? base.multiple,
    min: link.min ?? base.min,
    max: link.max ?? base.max,
    active: link.active ?? base.active,
  };

  return merged;
}

/**
 * The item's active options in menu order: its own options (price variants)
 * plus the linked ones with the link overrides applied.
 */
export function activeItemOptions(item: IItem): IOption[] {
  const mergedOptions: IOption[] = [];

  // Price variants (absolute prices)
  if (Array.isArray(item.options)) {
    mergedOptions.push(...item.options);
  }

  // Optionals/additionals via link table
  if (Array.isArray(item.itemOptions)) {
    for (const link of item.itemOptions) {
      const merged = mergeItemOptionLink(link);
      if (merged) mergedOptions.push(merged);
    }
  }

  return mergedOptions
    .filter((o) => !!o && !!o.active)
    .sort(compareByIndexThenId);
}

export function itemToSummary(params: {
  item: IItem;
  categoryName: string | null;
}): IGpthelpAdminItemSummary {
  const { item, categoryName } = params;
  return {
    itemId: item.id,
    name: item.name ?? '',
    description: toNullableString(item.description) ?? null,
    categoryId: item.categoryId,
    categoryName,
    menuId: toNullableNumber(item.menuId) ?? null,
    visible: !!item.visible,
    hasOptions: !!item.hasOptions,
    price: toNullableNumber(item.price) ?? null,
    discountedPrice: toNullableNumber(item.discountedPrice) ?? null,
    picture: toNullableString(item.picture) ?? null,
    index: toNullableNumber(item.index) ?? null,
    ...(item.popularHidden ? { notInPopular: true as const } : {}),
  };
}

/**
 * The product with every group and choice that is not removed (paused and
 * hidden ones included, each with its `active`), so the model can see and
 * name what the Menú editor shows.
 */
export function itemToDetails(params: {
  item: IItem;
  categoryName: string | null;
  /** Group id → products linking it (`optionUsage` over the store's items). */
  usage: TGpthelpOptionUsage;
}): IGpthelpAdminItemDetails {
  const { item, usage } = params;
  const options = allItemOptions(item).map((entry) =>
    optionToDetails(entry, usage)
  );

  const base = itemToSummary(params);

  return {
    ...base,
    preparationNotes: toNullableString(item.recipe) ?? null,
    hourRanges: Array.isArray(item.hourRanges) ? item.hourRanges : null,
    hiddenBranchesIds: Array.isArray(item.hiddenBranchesIds)
      ? item.hiddenBranchesIds
      : null,
    options,
  };
}
